import { MedicalInformationCountByType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { Accordion, StyledFlex } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { patientPaths } from '~libs.desktop.shared.paths';

import { patientMedicalInformationTranslationKeys } from '../translations';

import {
   MedicalInformationAccordionSectionContent,
   MedicalInformationAccordionSectionDecorator,
   MedicalInformationAccordionSectionHeader,
   MedicalInformationAccordionSectionLabel,
} from './MedicalInformationAccordion.components';
import { StyledStatusBadge } from './MedicalInformationAccordion.styled';
import { getMedicalInformationTypeByCategorySlug } from './MedicalInformationAccordion.utils';

type MedicalInformationAccordionProps = {
   readonly medicalInformationCountByTypes: MedicalInformationCountByType[];
   readonly patientId: number;
};

export const MedicalInformationAccordion = (props: MedicalInformationAccordionProps): JSX.Element => {
   const { category, entryId } = patientPaths.useMedicalDataSearchParams();

   const highlightedType = getMedicalInformationTypeByCategorySlug(category);
   const availableTypes = props.medicalInformationCountByTypes.map((countByType) => countByType.type);
   const defaultOpenedSectionId =
      highlightedType && availableTypes.includes(highlightedType) ? highlightedType : undefined;

   return (
      <Accordion testId={'medicalInformation'} defaultOpenedSectionsIds={defaultOpenedSectionId}>
         {props.medicalInformationCountByTypes.map((countByType) => (
            <Accordion.Section
               testId={countByType.type}
               key={countByType.type}
               id={countByType.type}
               label={({ opened }) => (
                  <MedicalInformationAccordionSectionHeader
                     label={
                        <MedicalInformationAccordionSectionLabel
                           medicalInformationType={countByType.type}
                           medicalInformationCount={countByType.medicalInformationCount}
                           patientId={props.patientId}
                        />
                     }
                     decorator={
                        <StyledFlex gap={4} alignItems={'center'}>
                           <MedicalInformationAccordionSectionDecorator
                              medicalInformationType={countByType.type}
                              open={opened}
                              unsignedAttachmentCount={countByType.unsignedAttachmentCount}
                           />

                           {countByType.medicalInformationRemoved ? (
                              <StyledStatusBadge testId={'medicalInformationStatus'} type={'unapproved'}>
                                 <Translate>{patientMedicalInformationTranslationKeys.removed}</Translate>
                              </StyledStatusBadge>
                           ) : null}
                        </StyledFlex>
                     }
                  />
               )}
            >
               <MedicalInformationAccordionSectionContent
                  medicalInformationType={countByType.type}
                  patientId={props.patientId}
                  highlightedEntryId={countByType.type === highlightedType ? entryId : undefined}
               />
            </Accordion.Section>
         ))}
      </Accordion>
   );
};
